import React, { useState, useEffect } from 'react';
import api from '../api/axiosConfig';
import StoryboardEditor from './StoryboardEditor';
import { useLanguage } from '../i18n/LanguageContext';
import './Storyboards.css';

interface Storyboard {
  id: number;
  name: string;
  description: string;
  created_at: string;
  updated_at: string;
  entry_count: number;
}

interface StoryboardsProps {
  onRequestCreate?: () => void;
  /** A board to open straight away — a link, or one shared with this user */
  initialStoryboardId?: number | null;
}

const Storyboards: React.FC<StoryboardsProps> = ({ onRequestCreate, initialStoryboardId }) => {
  const { t } = useLanguage();
  const [storyboards, setStoryboards] = useState<Storyboard[]>([]);
  const [loading, setLoading] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newStoryboardName, setNewStoryboardName] = useState('');
  const [newStoryboardDescription, setNewStoryboardDescription] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [selectedStoryboardId, setSelectedStoryboardId] = useState<number | null>(null);

  // Expose create modal trigger to parent
  React.useEffect(() => {
    if (onRequestCreate) {
      (window as any).__openStoryboardModal = () => setIsCreateModalOpen(true);
    }
    return () => {
      delete (window as any).__openStoryboardModal;
    };
  }, [onRequestCreate]);

  // Once, on mount: the list is refetched explicitly after every change.
  useEffect(() => {
    fetchStoryboards();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Only when the requested board changes: closing the editor must not be
  // undone by this effect re-opening the same board.
  useEffect(() => {
    if (initialStoryboardId) setSelectedStoryboardId(initialStoryboardId);
  }, [initialStoryboardId]);

  const fetchStoryboards = async () => {
    setLoading(true);
    try {
      const response = await api.get('/storyboards/');
      setStoryboards(response.data);
    } catch (error) {
      console.error('Failed to fetch storyboards:', error);
      setError(t.storyboards.loadFailed);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateStoryboard = async () => {
    if (!newStoryboardName.trim()) {
      setError(t.storyboards.nameRequired);
      return;
    }

    try {
      await api.post('/storyboards/', {
        name: newStoryboardName,
        description: newStoryboardDescription
      });

      setNewStoryboardName('');
      setNewStoryboardDescription('');
      setIsCreateModalOpen(false);
      setError(null);
      fetchStoryboards();
    } catch (error: any) {
      console.error('Failed to create storyboard:', error);
      setError(error.response?.data?.detail || t.storyboards.createFailed);
    }
  };

  const handleDeleteStoryboard = async (id: number) => {
    if (!window.confirm(t.storyboards.deleteConfirm)) {
      return;
    }

    try {
      await api.delete(`/storyboards/${id}/`);
      fetchStoryboards();
    } catch (error) {
      console.error('Failed to delete storyboard:', error);
      setError(t.storyboards.deleteFailed);
    }
  };

  const handleOpenStoryboard = (id: number) => {
    setSelectedStoryboardId(id);
  };

  const handleCloseEditor = () => {
    setSelectedStoryboardId(null);
    fetchStoryboards(); // Refresh list when closing editor
  };

  if (selectedStoryboardId) {
    return (
      <StoryboardEditor
        storyboardId={selectedStoryboardId}
        onClose={handleCloseEditor}
      />
    );
  }

  return (
    <div className="storyboards-container">
      <div className="storyboards-header">
        <h1>{t.storyboards.title}</h1>
        {!loading && storyboards.length > 0 && (
          <button
            className="create-storyboard-btn"
            onClick={() => setIsCreateModalOpen(true)}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            {t.storyboards.newStoryboard}
          </button>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <div className="loading">{t.storyboards.loading}</div>
      ) : storyboards.length === 0 ? (
        <div className="storyboards-empty">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <rect x="3" y="3" width="7" height="7"></rect>
            <rect x="14" y="3" width="7" height="7"></rect>
            <rect x="14" y="14" width="7" height="7"></rect>
            <rect x="3" y="14" width="7" height="7"></rect>
          </svg>
          <h2>{t.storyboards.noneYet}</h2>
          <p>{t.storyboards.createFirst}</p>
          <button
            className="create-first-btn"
            onClick={() => setIsCreateModalOpen(true)}
          >
            {t.storyboards.createStoryboard}
          </button>
        </div>
      ) : (
        <div className="storyboards-grid">
          {storyboards.map((storyboard) => (
            <div
              key={storyboard.id}
              className="storyboard-card"
              onClick={() => handleOpenStoryboard(storyboard.id)}
            >
              <div className="storyboard-card-header">
                <h3>{storyboard.name}</h3>
                <button
                  className="delete-storyboard-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteStoryboard(storyboard.id);
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="3 6 5 6 21 6"></polyline>
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                  </svg>
                </button>
              </div>
              {storyboard.description && (
                <p className="storyboard-description">{storyboard.description}</p>
              )}
              <div className="storyboard-meta">
                <span className="entry-count">
                  {storyboard.entry_count}{' '}
                  {storyboard.entry_count === 1
                    ? t.storyboards.entryOne
                    : t.storyboards.entryMany}
                </span>
                <span className="updated-date">
                  {t.storyboards.updated.replace(
                    '{date}',
                    new Date(storyboard.updated_at).toLocaleDateString()
                  )}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {isCreateModalOpen && (
        <div className="modal-overlay" onClick={() => setIsCreateModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{t.storyboards.createTitle}</h2>
            {error && <div className="modal-error">{error}</div>}
            <div className="form-group">
              <label>{t.storyboards.name}</label>
              <input
                type="text"
                value={newStoryboardName}
                onChange={(e) => setNewStoryboardName(e.target.value)}
                placeholder={t.storyboards.namePlaceholder}
                autoFocus
              />
            </div>
            <div className="form-group">
              <label>{t.storyboards.descriptionOptional}</label>
              <textarea
                value={newStoryboardDescription}
                onChange={(e) => setNewStoryboardDescription(e.target.value)}
                placeholder={t.storyboards.descriptionPlaceholder}
                rows={3}
              />
            </div>
            <div className="modal-actions">
              <button
                className="cancel-btn"
                onClick={() => {
                  setIsCreateModalOpen(false);
                  setNewStoryboardName('');
                  setNewStoryboardDescription('');
                  setError(null);
                }}
              >
                {t.common.cancel}
              </button>
              <button
                className="create-btn"
                onClick={handleCreateStoryboard}
              >
                {t.storyboards.create}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Storyboards;
